import { useState } from 'react';
import { loginWithEmail, loginWithGoogle, signupWithEmail, resetPassword } from '../services/firebase';

const BASE = import.meta.env.BASE_URL;
const IS_EXTENSION = new URLSearchParams(window.location.search).get('mode') === 'extension';

function RaapidLogo({ height = 44 }: { height?: number }) {
  return (
    <img
      src={`${BASE}raapid-logo.png`}
      alt="RAAPID"
      style={{ height: `${height}px`, objectFit: 'contain', display: 'block', margin: '0 auto' }}
    />
  );
}

type Mode = 'login' | 'signup';

export default function LoginPage() {
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await loginWithEmail(email, password);
      } else {
        await signupWithEmail(email, password);
      }
    } catch (err: unknown) {
      const msg = (err as { message?: string }).message ?? 'Something went wrong';
      setError(friendlyError(msg));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setLoading(true);
    try {
      await loginWithGoogle();
    } catch (err: unknown) {
      const msg = (err as { message?: string }).message ?? 'Something went wrong';
      setError(friendlyError(msg));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) { setError('Enter your email address first'); return; }
    setLoading(true);
    try {
      await resetPassword(email);
      setResetSent(true);
      setError('');
    } catch {
      setError('Could not send reset email. Check the address and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      height: '100%',
      display: 'flex',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>

      {/* ─── Left: Hero image (75%) ────────────────────────────────── */}
      <div style={{
        flex: 75,
        background: '#0B1120',
        position: 'relative',
        overflow: 'hidden',
        minHeight: '100vh',
      }}>
        <img
          src={`${BASE}login-hero-green.png`}
          alt="RAAPID Point-of-Care Coding"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      </div>

      {/* ─── Right: Login form (25%) ───────────────────────────────── */}
      <div style={{
        flex: 25,
        minWidth: '380px',
        maxWidth: '480px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '48px 32px',
        background: '#F1F5F9',
        position: 'relative',
        overflowY: 'auto',
        borderLeft: '1px solid #E2E8F0',
      }}>
        {/* Login card */}
        <div style={{
          width: '100%',
          maxWidth: '380px',
          background: '#FFFFFF',
          borderRadius: '16px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
          padding: '40px 32px 32px',
        }}>
          {/* Logo + Heading */}
          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <RaapidLogo height={52} />
            <h1 style={{ fontSize: '17px', fontWeight: 600, color: '#0F172A', margin: '16px 0 0' }}>
              {mode === 'login' ? 'Welcome back' : 'Create account'}
            </h1>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px',
              padding: '10px 14px', marginBottom: '16px',
              fontSize: '13px', color: '#DC2626',
            }}>
              {error}
            </div>
          )}

          {/* Reset sent */}
          {resetSent && (
            <div style={{
              background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px',
              padding: '10px 14px', marginBottom: '16px',
              fontSize: '13px', color: '#16A34A',
            }}>
              Password reset email sent — check your inbox.
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                Email address
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                  <MailIcon />
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  style={{
                    width: '100%',
                    padding: '11px 12px 11px 38px',
                    borderRadius: '8px',
                    border: '1px solid #E2E8F0',
                    fontSize: '14px',
                    color: '#0F172A',
                    background: '#FFFFFF',
                    outline: 'none',
                    transition: 'border-color 0.15s',
                    boxSizing: 'border-box',
                  }}
                  onFocus={e => e.target.style.borderColor = '#10B981'}
                  onBlur={e => e.target.style.borderColor = '#E2E8F0'}
                />
              </div>
            </div>

            {/* Password */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                  <LockSmallIcon />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  style={{
                    width: '100%',
                    padding: '11px 40px 11px 38px',
                    borderRadius: '8px',
                    border: '1px solid #E2E8F0',
                    fontSize: '14px',
                    color: '#0F172A',
                    background: '#FFFFFF',
                    outline: 'none',
                    transition: 'border-color 0.15s',
                    boxSizing: 'border-box',
                  }}
                  onFocus={e => e.target.style.borderColor = '#10B981'}
                  onBlur={e => e.target.style.borderColor = '#E2E8F0'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
                    color: '#94A3B8', display: 'flex',
                  }}
                >
                  <EyeIcon crossed={!showPassword} />
                </button>
              </div>
            </div>

            {/* Forgot password */}
            {mode === 'login' && (
              <div style={{ textAlign: 'right', marginBottom: '24px' }}>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  style={{ background: 'none', border: 'none', fontSize: '13px', color: '#059669', fontWeight: 600, cursor: 'pointer' }}
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              onMouseEnter={() => setHoveredBtn('login')}
              onMouseLeave={() => setHoveredBtn(null)}
              style={{
                width: '100%', padding: '13px 16px', borderRadius: '10px', border: 'none',
                background: hoveredBtn === 'login'
                  ? 'linear-gradient(135deg, #047857, #059669)'
                  : 'linear-gradient(135deg, #059669, #10B981)',
                color: '#FFFFFF', fontSize: '15px', fontWeight: 600,
                cursor: loading ? 'wait' : 'pointer', transition: 'all 0.15s ease',
                opacity: loading ? 0.6 : 1, boxShadow: '0 2px 8px rgba(5,150,105,0.3)',
                marginBottom: mode === 'signup' ? '24px' : 0,
              }}
            >
              {loading ? 'Please wait...' : mode === 'login' ? 'Sign in' : 'Create account'}
            </button>
          </form>

          {!IS_EXTENSION && (<>
          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: '20px 0' }}>
            <div style={{ flex: 1, height: '1px', background: '#E2E8F0' }} />
            <span style={{ color: '#94A3B8', fontSize: '12px', fontWeight: 500 }}>OR</span>
            <div style={{ flex: 1, height: '1px', background: '#E2E8F0' }} />
          </div>

          {/* Google */}
          <button
            onClick={handleGoogle}
            disabled={loading}
            onMouseEnter={() => setHoveredBtn('google')}
            onMouseLeave={() => setHoveredBtn(null)}
            style={{
              width: '100%', padding: '12px 16px', borderRadius: '10px',
              border: '1px solid #E2E8F0',
              background: hoveredBtn === 'google' ? '#F8FAFC' : '#FFFFFF',
              color: '#1E293B', fontSize: '14px', fontWeight: 500,
              cursor: loading ? 'wait' : 'pointer', transition: 'all 0.15s ease',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '12px', opacity: loading ? 0.6 : 1,
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            }}
          >
            <GoogleIcon />
            Continue with Google
          </button>
          </>)}

          {/* Toggle login / signup */}
          <p style={{ textAlign: 'center', marginTop: '24px', marginBottom: 0, fontSize: '13px', color: '#64748B' }}>
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button
              onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError(''); setResetSent(false); }}
              style={{ background: 'none', border: 'none', fontSize: '13px', color: '#059669', fontWeight: 600, cursor: 'pointer', padding: 0 }}
            >
              {mode === 'login' ? 'Create one' : 'Sign in'}
            </button>
          </p>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: '24px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <LockIcon />
            <span style={{ color: '#94A3B8', fontSize: '11px' }}>Secured by Auth0</span>
          </div>
          <span style={{ color: '#CBD5E1', fontSize: '11px' }}>·</span>
          <span style={{ color: '#94A3B8', fontSize: '11px' }}>Privacy</span>
          <span style={{ color: '#94A3B8', fontSize: '11px' }}>Terms</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Helpers ──────────────────────────────────────────────────────────────── */

function friendlyError(msg: string): string {
  if (msg.includes('user-not-found') || msg.includes('wrong-password') || msg.includes('invalid-credential'))
    return 'Incorrect email or password.';
  if (msg.includes('email-already-in-use'))
    return 'An account with this email already exists.';
  if (msg.includes('weak-password'))
    return 'Password must be at least 6 characters.';
  if (msg.includes('invalid-email'))
    return 'Please enter a valid email address.';
  if (msg.includes('popup-closed-by-user'))
    return '';
  if (msg.includes('network-request-failed'))
    return 'Network error — check your connection.';
  if (msg.includes('operation-not-allowed') || msg.includes('OPERATION_NOT_ALLOWED'))
    return 'Email/password sign-in is not enabled. Enable it in GCP Identity Platform → Providers.';
  if (msg.includes('configuration-not-found') || msg.includes('PROJECT_NOT_FOUND'))
    return 'Firebase project not found. Check VITE_FIREBASE_PROJECT_ID in .env';
  return `Error: ${msg}`;
}

/* ─── Icons ────────────────────────────────────────────────────────────────── */

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

function LockSmallIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function EyeIcon({ crossed }: { crossed: boolean }) {
  if (crossed) {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
        <path d="m1 1 22 22" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      </svg>
    );
  }
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

